import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import InventoryIcon from "@mui/icons-material/Inventory";
import { useTheme } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import CloseIcon from "@mui/icons-material/Close";
import {
  Edit,
  Delete,
  NotificationsActive,
  SpeedRounded,
  CompareArrowsRounded,
  NumbersRounded,
  WarningAmberRounded,
} from "@mui/icons-material";
import axios from "axios";

import { APP_API_V1_BASE } from "../../../config/api";
const API_BASE = `${APP_API_V1_BASE}`;

const EMPTY_FORM = {
  metric_key: "",
  operator: ">",
  threshold: "",
  severity: "warning",
};
const OPERATORS = [
  { value: ">", label: "Greater than", symbol: ">" },
  { value: "<", label: "Less than", symbol: "<" },
  { value: ">=", label: "Greater or equal", symbol: "≥" },
  { value: "<=", label: "Less or equal", symbol: "≤" },
  { value: "==", label: "Equal", symbol: "=" },
];

const SEVERITY_META = {
  critical: {
    color: "#EF4444",
    bg: "#FEE2E2",
    border: "#EF4444",
    dot: "#EF4444",
  },
  high: { color: "#F97316", bg: "#FFEDD5", border: "#F97316", dot: "#F97316" },
  warning: {
    color: "#F59E0B",
    bg: "#FEF3C7",
    border: "#F59E0B",
    dot: "#F59E0B",
  },
  info: { color: "#0EA5E9", bg: "#E0F2FE", border: "#0EA5E9", dot: "#0EA5E9" },
};
export default function RulesTab({ rules, device, headers, loadData }) {
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [metricCatalog, setMetricCatalog] = useState([]);
  const theme = useTheme();
  // ✅ FIX: Confirm delete dialog state
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // ✅ FIX: Toast feedback for errors
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const showError = (message) =>
    setToast({ open: true, message, severity: "error" });

  const showSuccess = (message) =>
    setToast({ open: true, message, severity: "success" });

  /* ---------------------------
     Toggle Rule
  --------------------------- */
  const toggleRule = async (id, currentState) => {
    try {
      await axios.patch(
        `${API_BASE}/devices/${device.id}/alerts/rules/${id}`,
        { enabled: !currentState },
        { headers },
      );
      loadData();
    } catch (err) {
      console.error("Toggle failed", err);
      showError("Failed to toggle rule. Please try again.");
    }
  };
  const CustomToolbar = ({ searchQuery, onSearch }) => {
    return (
      <Box sx={{ p: 1 }}>
        <TextField
          size="small"
          placeholder="Search..."
          value={searchQuery}
          onChange={(e) => onSearch(e.target.value)}
          fullWidth
        />
      </Box>
    );
  };
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearch = (value) => {
    setSearchQuery(value);

    // Optional: filter rules here
  };
  /* ---------------------------
     Delete Rule (with confirm)
  --------------------------- */
  const confirmDelete = (id) => setConfirmDeleteId(id);

  const deleteRule = async () => {
    try {
      await axios.delete(
        `${API_BASE}/devices/${device.id}/alerts/rules/${confirmDeleteId}`,
        { headers },
      );
      setConfirmDeleteId(null);
      showSuccess("Rule deleted.");
      loadData();
    } catch (err) {
      console.error("Delete failed", err);
      showError("Failed to delete rule. Please try again.");
    }
  };

  /* ---------------------------
     Open Create
  --------------------------- */
  const handleCreate = () => {
    setEditingRule(null);
    setFormData(EMPTY_FORM);
    setOpenDialog(true);
  };

  /* ---------------------------
     Open Edit
     ✅ FIX: Only pick editable fields — no id/org/device leaking into formData
  --------------------------- */
  const handleEdit = (rule) => {
    setEditingRule(rule);
    setFormData({
      metric_key: rule.metric_key,
      operator: rule.operator,
      threshold: rule.threshold,
      severity: rule.severity || "warning",
    });
    setOpenDialog(true);
  };

  /* ---------------------------
     Save Rule (Create / Update)
  --------------------------- */
  const handleSave = async () => {
    if (!formData.metric_key) return showError("Please select a metric.");
    if (!formData.threshold) return showError("Threshold is required.");
    if (isNaN(Number(formData.threshold)))
      return showError("Threshold must be a number.");

    try {
      setLoading(true);

      if (editingRule) {
        await axios.patch(
          `${API_BASE}/devices/${device.id}/alerts/rules/${editingRule.id}`,
          formData,
          { headers },
        );
      } else {
        await axios.post(
          `${API_BASE}/devices/${device.id}/alerts/rules`,
          formData,
          { headers },
        );
      }

      setOpenDialog(false);
      showSuccess(editingRule ? "Rule updated." : "Rule created.");
      loadData();
    } catch (err) {
      const message = err.response?.data?.error || "Failed to save rule.";
      showError(message);
    } finally {
      setLoading(false);
    }
  };
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    const loadMetrics = async () => {
      if (!device?.id) {
        setMetricCatalog([]);
        return;
      }

      try {
        const res = await axios.get(
          `${API_BASE}/devices/${device.id}/connection`,
          { headers },
        );
        setMetricCatalog(res.data?.metric_catalog || []);
      } catch {
        setMetricCatalog([]);
      }
    };

    loadMetrics();
  }, [device?.id, headers]);

  const metricOptions = useMemo(() => {
    const merged = [
      ...(Array.isArray(metricCatalog) ? metricCatalog : []),
      ...(Array.isArray(device?.schema) ? device.schema : []),
    ];
    const seen = new Set();
    return merged.filter((item) => {
      const key = item?.key || item?.metric_key;
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [metricCatalog, device?.schema]);

  /* ---------------------------
     ✅ FIX: Safe schema unit lookup — extracted to avoid repeated optional chaining
  --------------------------- */
  const getFieldMeta = (metricKey) => {
    const field = metricOptions.find((f) => f.key === metricKey);
    if (!field) return { unit: "" };
    let unit =
      field.unit === "Custom" ? field.customUnit || "" : field.unit || "";
    if (unit == "-") unit = ""; // clean up dash-only units
    return { unit };
  };

  const selectedFieldMeta = getFieldMeta(formData.metric_key);

  const severityMeta =
    SEVERITY_META[formData.severity] || SEVERITY_META.warning;

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2.5,
      background: "rgba(248,250,252,0.8)",
    },
  };

  const SectionLabel = ({ icon, children }) => (
    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
      {icon}
      <Typography fontSize={12} fontWeight={600} color="#64748B">
        {children}
      </Typography>
    </Box>
  );
  /* ---------------------------
     Table Columns
  --------------------------- */
  const columns = [
    {
      field: "SL No.",
      headerName: "SL No.",
      width: 90,
      renderCell: (params) =>
        params.api.getRowIndexRelativeToVisibleRows(params.id) + 1,
    },
    {
      field: "metric_key",
      headerName: "Metric",
      flex: 1,
      renderCell: (params) => {
        const { unit } = getFieldMeta(params.value);
        return (
          <span style={{ fontWeight: 600 }}>
            {params.value} {unit ? `(${unit})` : ""}
          </span>
        );
      },
    },
    { field: "operator", headerName: "Condition", width: 120 },
    {
      field: "threshold",
      headerName: "Threshold",
      width: 140,
      renderCell: (params) => {
        const { unit } = getFieldMeta(params.row.metric_key);
        return `${params.value}${unit ? ` ${unit}` : ""}`;
      },
    },
    {
      field: "severity",
      headerName: "Severity",
      width: 130,
      renderCell: (params) => {
        const colorMap = {
          critical: { color: "#D32F2F", bg: "#FDECEA" },
          high: { color: "#F57C00", bg: "#FFF4E5" },
          warning: { color: "#ED6C02", bg: "#FFF3E0" },
          info: { color: "#0288D1", bg: "#E3F2FD" },
        };
        const style = colorMap[params.value] || {};
        return (
          <Typography
            variant="caption"
            fontWeight={700}
            sx={{
              color: style.color,
              backgroundColor: style.bg,
              px: 1,
              py: 0.5,
              borderRadius: 1,
            }}
          >
            {params.value?.toUpperCase()}
          </Typography>
        );
      },
    },
    {
      field: "enabled",
      headerName: "Status",
      width: 100,
      renderCell: (params) => (
        <Switch
          checked={params.value}
          onChange={() => toggleRule(params.row.id, params.value)}
          size="small"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <Box gap={0.5}>
          <IconButton size="small" onClick={() => handleEdit(params.row)}>
            <Edit fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            color="error"
            onClick={() => confirmDelete(params.row.id)}
          >
            <Delete fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Paper sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Alert Rules</Typography>
        <Button variant="contained" onClick={handleCreate}>
          + Create Rule
        </Button>
      </Box>
      <Box
        sx={{
          height: rules.length === 0 ? 220 : 340,
          width: "100%",
        }}
      >
        <DataGrid
          rows={rules}
          columns={columns}
          getRowId={(r) => r.id}
          loading={loading}
          disableRowSelectionOnClick
          disableColumnMenu
          pageSizeOptions={[15, 20, 50, 100]}
          slots={{
            toolbar: () => (
              <CustomToolbar
                searchQuery={searchQuery}
                onSearch={handleSearch}
              />
            ),

            noRowsOverlay: () => (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="100%"
                py={6}
              >
                <InventoryIcon
                  sx={{ fontSize: 52, color: "text.disabled", mb: 2 }}
                />

                <Typography
                  variant="body1"
                  fontWeight={600}
                  color="text.secondary"
                  gutterBottom
                >
                  No rules created yet
                </Typography>

                <Typography variant="body2" color="text.disabled">
                  Click "Create Rule" to add your first rule
                </Typography>
              </Box>
            ),
          }}
          sx={{
            border: "none",

            "& .MuiDataGrid-columnHeaders": {
              backgroundColor:
                theme.palette.mode === "dark" ? "#1e293b" : "#f8fafc",
            },

            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
              color: "text.secondary",
              fontSize: 12,
            },

            "& .MuiDataGrid-row:hover": {
              backgroundColor:
                theme.palette.mode === "dark" ? "#334155" : "#f8fafc",
              cursor: "pointer",
            },

            "& .MuiDataGrid-cell:focus": { outline: "none" },
            "& .MuiDataGrid-cell:focus-within": { outline: "none" },
          }}
        />
      </Box>

      {/* ================= Create / Edit Dialog ================= */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 4,
          },
        }}
      >
        {/* HEADER */}
        <Box
          sx={{
            px: 3,
            py: 3,
            background:
              "linear-gradient(135deg, #0F172A, #0369A1 60%, #0F766E)",
            color: "#fff",
            position: "relative",
          }}
        >
          <IconButton
            onClick={() => setOpenDialog(false)}
            sx={{ position: "absolute", top: 12, right: 12, color: "#fff" }}
          >
            <CloseIcon />
          </IconButton>
          <Typography fontSize={26} fontWeight={800}>
            {" "}
            {editingRule ? "Edit Alert Rule" : "Create Alert Rule"}
          </Typography>{" "}
          {device?.name && (
            <Typography fontSize={12} mt={1} color="rgba(255,255,255,0.7)">
              {device.name}
            </Typography>
          )}
        </Box>

        {/* BODY */}
        <DialogContent sx={{ mt: 1 }}>
          <Stack spacing={3}>
            {/* Metric */}
            <Box>
              <SectionLabel icon={<SpeedRounded fontSize="small" />}>
                Metric
              </SectionLabel>

              <TextField
                select
                fullWidth
                size="small"
                value={formData.metric_key}
                onChange={(e) =>
                  setFormData({ ...formData, metric_key: e.target.value })
                }
                sx={fieldSx}
              >
                {metricOptions.map((field) => {
                  let unit =
                    field.unit === "Custom"
                      ? field.customUnit || ""
                      : field.unit || "";
                  if (unit === "-") unit = "";

                  return (
                    <MenuItem key={field.key} value={field.key}>
                      {field.key} {unit && `(${unit})`}
                    </MenuItem>
                  );
                })}
              </TextField>
            </Box>

            {/* Operator + Threshold */}
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
              <Box>
                <SectionLabel icon={<CompareArrowsRounded fontSize="small" />}>
                  Operator
                </SectionLabel>

                <TextField
                  select
                  fullWidth
                  size="small"
                  value={formData.operator}
                  onChange={(e) =>
                    setFormData({ ...formData, operator: e.target.value })
                  }
                  sx={fieldSx}
                >
                  {OPERATORS.map((op) => (
                    <MenuItem key={op.value} value={op.value}>
                      {op.symbol} — {op.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Box>

              <Box>
                <SectionLabel icon={<NumbersRounded fontSize="small" />}>
                  Threshold
                </SectionLabel>

                <TextField
                  type="number"
                  fullWidth
                  size="small"
                  value={formData.threshold}
                  onChange={(e) =>
                    setFormData({ ...formData, threshold: e.target.value })
                  }
                  sx={fieldSx}
                  InputProps={
                    selectedFieldMeta?.unit
                      ? {
                          endAdornment: (
                            <InputAdornment position="end">
                              {selectedFieldMeta.unit}
                            </InputAdornment>
                          ),
                        }
                      : undefined
                  }
                />
              </Box>
            </Box>

            {/* Severity */}
            <Box>
              <SectionLabel icon={<WarningAmberRounded fontSize="small" />}>
                Severity
              </SectionLabel>

              <Stack direction="row" spacing={1}>
                {Object.entries(SEVERITY_META).map(([key, meta]) => {
                  const active = formData.severity === key;

                  return (
                    <Box
                      key={key}
                      onClick={() =>
                        setFormData({ ...formData, severity: key })
                      }
                      sx={{
                        flex: 1,
                        py: 1,
                        borderRadius: 2,
                        textAlign: "center",
                        cursor: "pointer",
                        border: active
                          ? `2px solid ${meta.border}`
                          : "1px solid #E2E8F0",
                        background: active ? meta.bg : "#fff",
                      }}
                    >
                      <Typography
                        fontSize={12}
                        fontWeight={600}
                        color={active ? meta.color : "#64748B"}
                      >
                        {key}
                      </Typography>
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>

        {/* FOOTER */}
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
          }}
        >
          <Button
            onClick={() => setOpenDialog(false)}
           
          >
            Cancel
          </Button>

          
<Button
  variant="contained"
  onClick={handleSave}
  disabled={loading}
>

            {loading ? (
              <CircularProgress size={21} />
            ) : editingRule ? (
              "Save Changes"
            ) : (
              "Create Rule"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================= Confirm Delete Dialog ================= */}
      <Dialog open={!!confirmDeleteId} onClose={() => setConfirmDeleteId(null)}>
        <DialogTitle>Delete Rule?</DialogTitle>
        <DialogContent>
          <Typography>
            This rule will be permanently deleted. Active alerts triggered by
            this rule will remain in history.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={deleteRule}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          variant="filled"
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
}
